import React, { useRef, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { publicSignAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { ShieldCheck, FileText, PenLine, Type, Eraser, Loader2, CheckCircle2, ExternalLink, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function SignPublic() {
  const { token } = useParams();
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);
  const [loading, setLoading] = useState(true);
  const [req, setReq] = useState(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('draw');
  const [name, setName] = useState('');
  const [typed, setTyped] = useState('');
  const [place, setPlace] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    publicSignAPI.getRequest(token)
      .then(res => { setReq(res.data); setName(res.data.signer_name || ''); setTyped(res.data.signer_name || ''); if (res.data.status === 'signed') setDone(true); })
      .catch(e => setError(e?.response?.data?.detail || 'Signatur-Link ungültig'))
      .finally(() => setLoading(false));
  }, [token]);

  const clearCanvas = () => { const c = canvasRef.current; if (!c) return; c.getContext('2d').clearRect(0, 0, c.width, c.height); hasDrawn.current = false; };
  const pos = (e) => { const c = canvasRef.current, r = c.getBoundingClientRect(), t = e.touches ? e.touches[0] : e; return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) }; };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.strokeStyle = '#0a2870'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.stroke(); hasDrawn.current = true; };
  const end = () => { drawing.current = false; };
  const typedPng = () => { const c = document.createElement('canvas'); c.width = 500; c.height = 160; const ctx = c.getContext('2d'); ctx.fillStyle = '#0a2870'; ctx.font = 'italic 60px "Segoe Script","Brush Script MT",cursive'; ctx.textBaseline = 'middle'; ctx.fillText(typed || name, 20, 90); return c.toDataURL('image/png'); };

  const submit = async () => {
    if (!name.trim()) { toast.error('Bitte deinen Namen angeben'); return; }
    let sig;
    if (mode === 'draw') { if (!hasDrawn.current) { toast.error('Bitte unterschreiben'); return; } sig = canvasRef.current.toDataURL('image/png'); }
    else { if (!typed.trim()) { toast.error('Bitte Namen tippen'); return; } sig = typedPng(); }
    setSaving(true);
    try {
      await publicSignAPI.sign(token, { signature_image_base64: sig, signer_name: name.trim(), place: place.trim() || null });
      setDone(true); window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Signieren fehlgeschlagen'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="max-w-2xl mx-auto p-8 space-y-4"><Skeleton className="h-10 w-2/3" /><Skeleton className="h-64" /></div>;

  const waiting = req?.status === 'waiting';

  if (error) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="max-w-md w-full"><CardContent className="p-10 text-center space-y-3">
        <p className="text-muted-foreground">{error}</p>
      </CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="sign-public-page">
      <header className="border-b bg-card">
        <div className="max-w-2xl mx-auto px-6 py-6 flex items-center gap-2 text-primary font-heading font-bold">
          <ShieldCheck className="h-5 w-5" /> Dokument signieren
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-6 py-8">
        {done ? (
          <Card className="border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30" data-testid="sign-success">
            <CardContent className="p-10 text-center space-y-3">
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
              <h2 className="font-heading font-semibold text-xl">Vielen Dank!</h2>
              <p className="text-muted-foreground">Das Dokument „{req?.doc_title}" wurde erfolgreich signiert.</p>
            </CardContent>
          </Card>
        ) : waiting ? (
          <Card data-testid="sign-waiting">
            <CardContent className="p-10 text-center space-y-3">
              <Clock className="h-12 w-12 mx-auto text-amber-500" />
              <h2 className="font-heading font-semibold text-xl">Noch nicht an der Reihe</h2>
              <p className="text-muted-foreground">
                Das Dokument „{req?.doc_title}" wartet aktuell auf die Unterschrift
                {req?.waiting_for ? ` von „${req.waiting_for}"` : ' einer anderen Partei'}.
                Sobald Sie an der Reihe sind, erhalten Sie eine Benachrichtigung.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-6 space-y-4">
              <div>
                <h1 className="font-heading font-semibold text-lg">{req?.doc_title}</h1>
                {req?.role_label && <p className="text-sm text-muted-foreground">Ihre Rolle: <span className="font-medium">{req.role_label}</span></p>}
                <a href={publicSignAPI.pdfUrl(token)} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline mt-1" data-testid="view-doc-link">
                  <FileText className="h-4 w-4" /> Dokument ansehen <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>

              <div className="flex gap-2">
                <Button size="sm" variant={mode === 'draw' ? 'default' : 'outline'} onClick={() => setMode('draw')} data-testid="pub-mode-draw"><PenLine className="h-4 w-4 mr-1" /> Zeichnen</Button>
                <Button size="sm" variant={mode === 'type' ? 'default' : 'outline'} onClick={() => setMode('type')} data-testid="pub-mode-type"><Type className="h-4 w-4 mr-1" /> Tippen</Button>
              </div>

              {mode === 'draw' ? (
                <div className="space-y-2">
                  <div className="border rounded-lg bg-muted/20">
                    <canvas ref={canvasRef} width={500} height={160} className="w-full touch-none rounded-lg" style={{ height: 160 }}
                      onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                      onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="pub-canvas" />
                  </div>
                  <Button size="sm" variant="ghost" onClick={clearCanvas}><Eraser className="h-4 w-4 mr-1" /> Löschen</Button>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label>Unterschrift (getippt)</Label>
                  <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Name tippen" data-testid="pub-typed" />
                  <div className="border rounded-lg p-3 text-center bg-muted/20" style={{ fontFamily: '"Segoe Script","Brush Script MT",cursive', fontStyle: 'italic', fontSize: 32, color: '#0a2870' }}>{typed || 'Vorschau'}</div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Dein Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="pub-name" /></div>
                <div className="space-y-1.5"><Label>Ort (optional)</Label><Input value={place} onChange={(e) => setPlace(e.target.value)} data-testid="pub-place" /></div>
              </div>

              <p className="text-[11px] text-muted-foreground">Mit dem Signieren wird eine einfache elektronische Signatur (EES) mit Zeitstempel und Dokument-Hash erzeugt.</p>
              <Button className="w-full" onClick={submit} disabled={saving} data-testid="pub-submit">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Rechtsverbindlich signieren
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
      <footer className="border-t mt-8"><div className="max-w-2xl mx-auto px-6 py-4 text-xs text-muted-foreground">Powered by TeamHub</div></footer>
    </div>
  );
}
